import { TriangleAlert } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useMarkets } from '@/api/domains/markets';
import { useMe } from '@/api/domains/merchant-settings';
import type { Market } from '@/api/types/services';
import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { HIGHLIGHT_RING, useHighlightSection } from '@/hooks/use-highlight-section';
import { cn } from '@/lib/utils';

import { AddMarketDialog } from './AddMarketDialog';
import { AddMarketIcon } from './components/MethodIcons';
import { MarketSection } from './MarketSection';
import { MarketsEmptyState } from './MarketsEmptyState';

export function MarketsPage() {
  const { t } = useTranslation('markets');
  const tCommon = useTranslation('common').t;
  usePageTitle(t('title'));
  const marketsQuery = useMarkets();
  const meQuery = useMe();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Market | null>(null);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((market: Market) => {
    setEditing(market);
    setDialogOpen(true);
  }, []);

  const markets = marketsQuery.data ?? [];
  const originMissing = meQuery.isSuccess && !meQuery.data.settings.sender_details?.country_code;

  const methodsHighlight = useHighlightSection('delivery-methods');

  return (
    <div className="space-y-6">
      {/* Figma header: 36px medium title, actions right, subtitle below (no actions when empty). */}
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-4xl font-medium tracking-[-0.9px] text-black">{t('title')}</h2>
          {markets.length > 0 ? (
            <div className="flex items-center gap-3">
              {/* Visual parity with the design; the tour itself is not implemented yet. */}
              <Button
                variant="outline"
                className="border-black text-black hover:bg-accent"
                type="button"
              >
                {t('quickTour')}
              </Button>
              <Button onClick={openCreate}>{t('addMarket')}</Button>
            </div>
          ) : null}
        </div>
        <p className="text-base text-muted-foreground">{t('subtitle')}</p>
      </div>

      {originMissing ? (
        <Card className="border-amber-300/70 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-950/30">
          <CardContent className="flex flex-wrap items-center gap-3 py-4">
            <TriangleAlert className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-[16rem] flex-1">
              <p className="text-sm font-medium">{t('noOrigin.title')}</p>
              <p className="text-sm text-muted-foreground">{t('noOrigin.description')}</p>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings?tab=general">{t('noOrigin.cta')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <div
        ref={methodsHighlight.ref}
        className={cn('space-y-6 rounded-xl', methodsHighlight.isHighlighted && HIGHLIGHT_RING)}
      >
        {marketsQuery.isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : marketsQuery.isError ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <p className="text-sm text-muted-foreground">{t('loadError')}</p>
              <Button variant="outline" onClick={() => void marketsQuery.refetch()}>
                {tCommon('actions.retry')}
              </Button>
            </CardContent>
          </Card>
        ) : markets.length === 0 ? (
          <MarketsEmptyState onAdd={openCreate} />
        ) : (
          <>
            <div>
              {markets.map((market) => (
                <MarketSection key={market.id} market={market} onEdit={openEdit} />
              ))}
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="flex w-full items-center justify-center gap-3.5 rounded-[4px] border border-[#f2f2f2] p-6 transition-colors hover:bg-muted/40"
            >
              <span className="flex size-[33px] items-center justify-center rounded-xl bg-[#f7f7f7]">
                <AddMarketIcon className="size-[21px] text-black" />
              </span>
              <span className="text-sm font-medium text-black">{t('addMarket')}</span>
            </button>
          </>
        )}
      </div>

      <AddMarketDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} />
    </div>
  );
}
